import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Motif } from '../../types/motifs'
import { useContextUI } from '../../contexts/UIContext'
import { useCurrentOrigin } from '../../utils/useOrigin'
import { useEnv } from '../../utils/useEnv'
import { GalleryWrapper } from './styled'
import Lightbox from '../Lightbox'
import { useStrapiTranslation } from '../../utils/useStrapiTranslation'

interface Props {
  motif: Motif
}

const MotifGallerySection = ({ motif }: Props) => {
  const { t } = useTranslation()
  const { strapiTranslate } = useStrapiTranslation()
  const { showSnackbar } = useContextUI()
  const { isKiosk } = useEnv()
  const origin = useCurrentOrigin()

  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  // Create gallery images array from main photo and additional photos
  const galleryImages = [motif.photoUrl, ...(motif.AdditionalPhotos || [])].filter(
    Boolean,
  ) as string[]

  const openLightbox = (index: number = 0) => {
    setSelectedImageIndex(index)
    setLightboxOpen(true)
  }

  const closeLightbox = () => {
    setLightboxOpen(false)
    setSelectedImageIndex(0)
  }

  const goToPrevious = () => {
    const newIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : galleryImages.length - 1
    setSelectedImageIndex(newIndex)
  }

  const goToNext = () => {
    const newIndex = selectedImageIndex < galleryImages.length - 1 ? selectedImageIndex + 1 : 0
    setSelectedImageIndex(newIndex)
  }

  const selectImage = (index: number) => {
    setSelectedImageIndex(index)
  }

  return (
    <GalleryWrapper>
      <div
        className="image-section"
        data-id={motif.id}
        data-additional-photos={motif.AdditionalPhotos?.join(',')}
      >
        {motif.photoUrl && (
          <img
            className="main-photo"
            src={motif.photoUrl}
            alt={motif.title}
            onClick={() => openLightbox(0)}
          />
        )}
      </div>

      <div className="info-section">
        {/* <span className="artifact-placing">
          <strong>{t('artifactPlacing')} </strong>
        </span> */}
        <span className="inventory-number">
          {t('inventoryNumber')}: {motif.inventoryNumber}
        </span>

        {!isKiosk && (
          <span className="link-to-motif">
            <button
              className="link-to-motif-gallery"
              onClick={async e => {
                try {
                  const title = String(strapiTranslate(motif, 'title') || '').replace(/\s+/g, '_')
                  await navigator.clipboard.writeText(origin + '/' + motif.id + '_' + title)
                  showSnackbar(t('copyLinkSuccess'))
                } catch (error) {
                  showSnackbar(t('copyLinkFailure'))
                }
              }}
            >
              <img src="/icons/message-square-share.svg" alt="Share Icon" className="share-icon" />
              {t('copyLink')}
            </button>
          </span>
        )}
      </div>

      <div className="thumbnail-gallery">
        {galleryImages.slice(1).map((imageUrl, index) => (
          <img
            key={index + 1}
            className="thumbnail"
            src={imageUrl}
            alt={`${motif.title} - thumbnail ${index + 2}`}
            onClick={() => openLightbox(index + 1)}
          />
        ))}
      </div>

      <Lightbox
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        currentImage={galleryImages[selectedImageIndex] || ''}
        images={galleryImages}
        currentIndex={selectedImageIndex}
        onPrevious={goToPrevious}
        onNext={goToNext}
        onSelectImage={selectImage}
        inventoryNumber={motif.inventoryNumber}
        altText={motif.title}
      />
    </GalleryWrapper>
  )
}

export default MotifGallerySection
